import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { RegionalShell } from "@/components/cccd/RegionalShell"
import { SectionHeader } from "@/components/cccd/SectionHeader"
import { StatusPill } from "@/components/cccd/StatusPill"
import Alert from "@/components/ui/alert/Alert"
import { cccdAdmin } from "@/lib/api/cccdAdminClient"
import { getValidationErrorMessage } from "@/lib/errors"
import { slugify } from "@/lib/slugify"
import { toastError } from "@/lib/toast"
import { durationPill, registrationWindowPill } from "@/lib/cccdDatePills"

const emptyForm = {
  name: "",
  slug: "",
  region: "",
  description: "",
  eventStartDate: "",
  eventEndDate: "",
  registrationStartDate: "",
  registrationDeadline: "",
  consentText: "",
}

export default function RegionalFormPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const fromId = searchParams.get("fromId")
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState("")

  const { data: source } = useQuery({
    queryKey: ["cccd-regional", fromId],
    queryFn: () => cccdAdmin.getRegional(fromId!),
    enabled: Boolean(fromId),
  })

  useEffect(() => {
    if (!source) return
    setForm((prev) => ({
      ...prev,
      name: source.name,
      slug: slugify(source.name),
      consentText: source.consentText,
    }))
  }, [source])

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      fromId ? cccdAdmin.duplicateRegional(fromId, body) : cccdAdmin.createRegional(body),
    onSuccess: () => {
      // El destino es la lista, no un detalle — invalidar aquí es lo que hace
      // que el regional recién creado aparezca sin un refresh manual.
      qc.invalidateQueries({ queryKey: ["server-table", "/cccd/admin/regionals"] })
      navigate("/cccd/regionals")
    },
    onError: (err) => {
      const message = getValidationErrorMessage(err)
      setError(message)
      toastError(message)
    },
  })

  function f(key: keyof typeof emptyForm) {
    return {
      value: form[key],
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value })),
    }
  }

  function goBack() {
    navigate("/cccd/regionals")
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError("")
    const shared = {
      name: form.name,
      slug: form.slug,
      eventStartDate: new Date(form.eventStartDate).toISOString(),
      eventEndDate: new Date(form.eventEndDate).toISOString(),
      registrationStartDate: new Date(form.registrationStartDate).toISOString(),
      registrationDeadline: new Date(form.registrationDeadline).toISOString(),
      consentText: form.consentText,
    }
    // region/description/categorySlug/seedbedId no van en el body al duplicar
    // — el backend los clona del regional origen (cccd-seedbed-team-sync).
    createMutation.mutate(fromId ? shared : { ...shared, region: form.region, description: form.description })
  }

  const inputCls = "w-full border border-gray-300 dark:border-gray-700 rounded-lg p-2 text-sm bg-white dark:bg-gray-800"
  const eventDuration = durationPill(form.eventStartDate, form.eventEndDate)
  const registrationWindow = registrationWindowPill(form.registrationStartDate, form.registrationDeadline)

  return (
    <RegionalShell
      mode="create"
      activeTab="info"
      onTabChange={() => {}}
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        { label: "CCCD", to: "/cccd" },
        { label: "Regionales", to: "/cccd/regionals" },
        fromId ? "Duplicar regional" : "Nuevo regional",
      ]}
      title={fromId ? "Duplicar evento regional" : "Nuevo evento regional"}
      subtitle="Concurso regional CCCD y su calendario de inscripciones"
    >
      {fromId && source ? (
        <div className="mb-5">
          <Alert
            variant="info"
            title="Clonando estructura de una edición anterior"
            message={`Región (${source.region}), descripción y el semillero asignado de "${source.name}" se copiarán automáticamente. Cambia el nombre (el slug se genera solo a partir de él y debe ser distinto al del regional original), y define las fechas de esta nueva edición. Las categorías y precios se configuran después de guardar, en la pestaña Configuración.`}
          />
        </div>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-4">
        <SectionHeader
          title="Identificación"
          subtitle="Nombre y región de este evento regional"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.8" /><path d="M4 10h16M9 4v6" stroke="currentColor" strokeWidth="1.8" /></svg>}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-gray-500">Nombre del regional *</label>
              <input
                required
                className={inputCls}
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value
                  setForm((prev) => ({ ...prev, name, slug: slugify(name) }))
                }}
              />
            </div>
            {fromId ? (
              <div>
                <label className="mb-1 block text-xs text-gray-500">Slug</label>
                <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  <span className="font-mono">{form.slug || "—"}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(autogenerado)</span>
                </p>
              </div>
            ) : (
              <div>
                <label className="mb-1 block text-xs text-gray-500">Región *</label>
                <input required className={inputCls} {...f("region")} />
              </div>
            )}
          </div>
        </SectionHeader>

        <SectionHeader
          title="Fecha del concurso"
          subtitle="Cuándo se corre el regional presencialmente"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" /></svg>}
          badge={eventDuration && <StatusPill tone={eventDuration.tone} label={eventDuration.label} />}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-gray-500">Inicio del evento *</label>
              <input required type="datetime-local" className={inputCls} {...f("eventStartDate")} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Fin del evento *</label>
              <input required type="datetime-local" className={inputCls} {...f("eventEndDate")} />
            </div>
          </div>
        </SectionHeader>

        <SectionHeader
          title="Inscripciones"
          subtitle="Ventana en la que los equipos pueden registrarse"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /></svg>}
          badge={registrationWindow && <StatusPill tone={registrationWindow.tone} label={registrationWindow.label} />}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-gray-500">Apertura de inscripciones *</label>
              <input required type="datetime-local" className={inputCls} {...f("registrationStartDate")} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Cierre de inscripciones *</label>
              <input required type="datetime-local" className={inputCls} {...f("registrationDeadline")} />
            </div>
          </div>
        </SectionHeader>

        {fromId ? null : (
          <SectionHeader
            title="Descripción"
            subtitle="Cómo se presenta este regional a los equipos"
            icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.8" /><path d="M4 10h16M9 4v6" stroke="currentColor" strokeWidth="1.8" /></svg>}
          >
            <textarea required rows={2} className={inputCls} {...f("description")} />
          </SectionHeader>
        )}

        <SectionHeader
          title="Consentimiento"
          subtitle="Texto legal que el equipo debe aceptar al inscribirse"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /></svg>}
        >
          <textarea required rows={3} className={inputCls} {...f("consentText")} />
        </SectionHeader>

        {error ? <Alert variant="error" title="No se pudo crear el regional" message={error} /> : null}

        <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
          <button type="button" onClick={goBack} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800">
            Cancelar
          </button>
          <button type="submit" disabled={createMutation.isPending} className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
            {createMutation.isPending ? "Creando…" : fromId ? "Duplicar regional" : "Crear regional"}
          </button>
        </div>
      </form>
    </RegionalShell>
  )
}
